import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { ShoppingCart } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import CartView from '../CartView';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ShareDropdown from '../Common/ShareDropdown';
import { Search, ShoppingCart, Star, Facebook, Twitter, Instagram, Menu, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import axios from '../../utilities/axios';
import { ReactComponent as ShoppingBag } from '../../assets/images/icons/shopping-bag_1.svg';
import * as API_END_POINT from '../../utilities/constant';
import {
    searchProducts,
    isSearchEnabled,
    autocomplete,
    getSuggestionAheadText,
    getSuggestionTabValue,
    getSuggestionCompletedValue,
    buildSuggestionLabelLookup,
    formatSuggestionDisplayText,
} from '../../services/searchApi';
import { fetchProductsWithFallback } from '../../utilities/apiHelper';
import { Helmet } from 'react-helmet';
import shopDot from '../../assets/images/logo.svg'
import { debounce } from 'lodash';
import { setAffiliateData, getAffiliateData } from '../../utilities/affiliate';

import logoShopdot from '../../assets/images/logo-png.png';
import HeaderSkeleton from './HeaderSkeleton';
import { DEFAULT_THEME, accentTextOf } from '../../pages/StoreBuilder/bannerMarkup';
import { announcementBarColors, lightToneOf, readableOn } from '../../shared/theme/storefrontTheme';

const SEARCH_FILTERS = [
    { field: 'status', operator: 'in', value: ['1'] },
    { field: 'shopify_status', operator: 'in', value: ['active'] },
];

const getMatchedHighlightText = (hit) => {
    const matchedData = hit?.matchedData || {};
    const columns = String(hit?.matchedColumns || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
    for (const column of columns) {
        const value = matchedData[column];
        if (value) return value;
    }
    return matchedData.title || hit?._formatted?.title || hit?.title || '';
};

const getProductStoreName = (product) =>
    product?.is_multi_brand
        ? product?.vendor?.name || product?.shopify_vendor?.shopdot_vendor?.display_name
        : product?.brand?.name || product?.brand_name;

const useManageKey = () => {
    const location = useLocation();

    useEffect(() => {
        const specificPage = '/checkout/payment-details';
        if (location.pathname === specificPage) {
            let existingKey = localStorage.getItem('idempotency_id');
            if (!existingKey) {
                existingKey = crypto.randomUUID();
                localStorage.setItem('idempotency_id', existingKey);
            }
        } else {
            const newKey = crypto.randomUUID();
            localStorage.setItem('idempotency_id', newKey);
        }
    }, [location.pathname]);
};


/* The "Near black" Header choice from the Style tab, in the designer's values.
   Scoped to the bar itself so the panels and drawers it opens keep their own
   light surfaces. */
const HEADER_DARK_STYLES = `
  header.sf-head-dark { background: #141210; }
  header.sf-head-dark a,
  header.sf-head-dark button,
  header.sf-head-dark span,
  header.sf-head-dark svg { color: rgba(255,255,255,0.82); }
  header.sf-head-dark a:hover,
  header.sf-head-dark button:hover { color: #FFFFFF; }
  /* The categories menu and the search results float below the bar as their own
     white surfaces, so they keep the light palette. */
  header.sf-head-dark .bg-white,
  header.sf-head-dark .bg-white a,
  header.sf-head-dark .bg-white button,
  header.sf-head-dark .bg-white span,
  header.sf-head-dark .bg-white svg { color: #2C2F32; }
  /* Same for the search strip under the bar. Buttons are excluded: the strip's
     own Search button is white on the store accent. The mobile nav is not here
     — it is part of the bar now, so it takes the bar's tone. */
  header.sf-head-dark .sf-search-row,
  header.sf-head-dark .sf-search-row a,
  header.sf-head-dark .sf-search-row span,
  header.sf-head-dark .sf-search-row svg { color: #565E64; }
`;

/* The mobile navigation, ported from the designer canvas's .sf-mobnav. The
   canvas opens it with a <details> because it cannot run scripts; here it is
   React state, with the canvas's metrics. It sits in the header's normal flow
   rather than position:absolute, so it pushes the page down instead of floating
   over it — as SP-main's own .sf-mobnav does. */
const MOBILE_NAV_STYLES = `
  header .sf-mobnav {
    /* The box is stated, not inherited. On the home route the published
       html_content injects the canvas's own .sf-mobnav rule (display:none,
       position:absolute, ...) into this document. This panel shares the class
       but not the mechanism, so anything left undeclared here is taken from
       that sheet — which is how the burger once opened a panel that computed
       display:none on the home page only. */
    display: block;
    position: static;
    padding: 0;
    gap: 0;
    width: 100%;
    box-sizing: border-box;
    border-top: 1px solid #E5E7E8;
    /* A long category list must not grow the sticky bar past the viewport, or
       the bottom rows become unreachable: the panel scrolls itself. */
    max-height: calc(100vh - 64px);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  header .sf-mobnav .sf-mobrow {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    padding: 12px 20px;
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    border-bottom: 1px solid #E5E7E8;
    color: #565E64;
  }
  header .sf-mobnav .sf-mobrow.is-on { color: #2C2F32; }
  header .sf-mobnav .sf-mobrow { transition: background-color .14s, color .14s; }
  header .sf-mobnav .sf-mobrow:hover,
  header .sf-mobnav .sf-mobrow:focus-visible { background: #F5F6F6; color: #2C2F32; }
  /* Inset: the row is full-bleed against the bar's edge, so an outset ring
     would be clipped by the panel's scroll box. */
  header .sf-mobnav .sf-mobrow:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: -2px; }
  header .sf-mobnav .sf-mobrow svg { width: 18px; height: 18px; flex: none; transition: transform .18s ease; }
  header .sf-mobnav .sf-mobrow[aria-expanded="true"] svg { transform: rotate(180deg); }
  /* Second level: one full-width row per category, nested under Categories.
     A wrapped inline list read as stray text rather than a menu and gave each
     name a thumb target the height of its own line. Rows keep the 44px tap
     target, an inset marks them as children, and the open parent row stays
     lit so the pair reads as one group. */
  header .sf-mobnav .sf-mobrow[aria-expanded="true"] { background: #FAFBFB; color: #2C2F32; border-bottom-color: transparent; }
  header .sf-mobnav .sf-mobcats {
    display: flex;
    flex-direction: column;
    background: #FAFBFB;
    border-bottom: 1px solid #E5E7E8;
  }
  header .sf-mobnav .sf-mobcat {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 10px 20px 10px 36px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #565E64;
    text-align: left;
    border-top: 1px solid #EEF0F1;
    transition: background-color .14s, color .14s;
  }
  header .sf-mobnav .sf-mobcat:first-child { border-top: 0; }
  header .sf-mobnav .sf-mobcat .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  header .sf-mobnav .sf-mobcat .ct { flex: none; font-size: 12px; color: #808B90; font-variant-numeric: tabular-nums; }
  header .sf-mobnav .sf-mobcat svg { width: 16px; height: 16px; flex: none; color: #9AA0A3; }
  header .sf-mobnav .sf-mobcat:hover,
  header .sf-mobnav .sf-mobcat:focus-visible { background: #F2F3F3; color: #2C2F32; }
  header .sf-mobnav .sf-mobcat:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: -2px; }
  /* The category you are browsing: darker text and a tick in the store's own
     colour (--sf-accent, set on the nav) on the inset edge, so the list also
     answers "where am I". */
  header .sf-mobnav .sf-mobcat.is-on { color: #2C2F32; font-weight: 500; }
  header .sf-mobnav .sf-mobcat.is-on::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: var(--sf-accent, ${DEFAULT_THEME});
  }
  /* Translucent white separators, not the light tone's #E5E7E8 — a grey
     hairline on #141210 reads as a scratch. */
  header.sf-head-dark .sf-mobnav,
  header.sf-head-dark .sf-mobnav .sf-mobrow,
  header.sf-head-dark .sf-mobnav .sf-mobcats { border-color: rgba(255,255,255,0.12); }
  header.sf-head-dark .sf-mobnav .sf-mobrow { color: rgba(255,255,255,0.82); }
  header.sf-head-dark .sf-mobnav .sf-mobrow.is-on { color: #FFFFFF; }
  /* On the near-black bar the hover goes to full white, as the designer's dark
     header and SP-main's accent nav do. Stated above the light-tone rules. */
  header.sf-head-dark .sf-mobnav .sf-mobrow:hover,
  header.sf-head-dark .sf-mobnav .sf-mobrow:focus-visible { background: rgba(255,255,255,0.06); color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobrow:focus-visible { outline-color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobrow[aria-expanded="true"],
  header.sf-head-dark .sf-mobnav .sf-mobcats { background: rgba(255,255,255,0.04); }
  header.sf-head-dark .sf-mobnav .sf-mobrow[aria-expanded="true"] { color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobcat { color: rgba(255,255,255,0.82); border-top-color: rgba(255,255,255,0.08); }
  header.sf-head-dark .sf-mobnav .sf-mobcat .ct,
  header.sf-head-dark .sf-mobnav .sf-mobcat svg { color: rgba(255,255,255,0.5); }
  header.sf-head-dark .sf-mobnav .sf-mobcat:hover,
  header.sf-head-dark .sf-mobnav .sf-mobcat:focus-visible { background: rgba(255,255,255,0.06); color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobcat:focus-visible { outline-color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobcat.is-on { color: #FFFFFF; }
  header.sf-head-dark .sf-mobnav .sf-mobcat.is-on::before { background: #FFFFFF; }
`;

/* The search and cart chips.

   ⚠ SPECIFICITY NOTE, which the nav links, the hover block and the categories
   panel below all depend on too: on the home route the designer's published
   stylesheet is injected into this document and lands AFTER Tailwind in source
   order, so a bare utility class ties on specificity and loses. Everything that
   the injected sheet also styles is therefore stated here, scoped through
   'header'. Here it is that sheet's own '.sf-ic { background: none; border: 1px
   solid transparent }', because the designer colors its chips inline. */
const HEADER_STYLES = `
  /* The bar's bottom edge, the canvas's navBorder: a hairline on the light
     tone, nothing on the dark one, where #141210 against the page is the edge.
     Stated at (0,1,1) because Tailwind's preflight zeroes border widths. */
  header.sf-head { border-bottom: 1px solid #E5E7E8; }
  header.sf-head.sf-head-dark { border-bottom: none; }
  header .sf-ic {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid #E5E7E8;
    background: #FFFFFF;
    color: #565E64;
    display: grid;
    place-items: center;
    position: relative;
    flex: none;
    padding: 0;
    cursor: pointer;
  }
  /* The glyph inherits the chip's color rather than the dark bar's blanket svg
     color, which on the white chip would paint it white on white. */
  header .sf-ic svg { width: 17px; height: 17px; color: inherit; }
  header.sf-head-dark .sf-ic {
    background: rgba(255,255,255,0.14);
    border-color: rgba(255,255,255,0.34);
    color: #FFFFFF;
  }
  /* The burger stays the reference's solid-white square on both tones — the one
     control that stays light on the dark bar, which marks it as the thing that
     opens something. It needs its own glyph color for the same reason the chips
     do. */
  header .sf-burger {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid #E5E7E8;
    background: #FFFFFF;
    color: #2C2F32;
    display: grid;
    place-items: center;
    flex: none;
    padding: 0;
    cursor: pointer;
  }
  header .sf-burger svg { width: 18px; height: 18px; color: inherit; }
  /* Restated for the dark bar: header.sf-head-dark button is (0,1,2) and
     outranks header .sf-burger at (0,1,1), so without this the glyph came out
     white on the white square. Same values, higher specificity. */
  header.sf-head-dark .sf-burger {
    background: #FFFFFF;
    border-color: #E5E7E8;
    color: #2C2F32;
  }
  header.sf-head-dark .sf-burger svg {
    color: #2C2F32;
  }

  /* ---- hover / focus ----------------------------------------------------
     Stated at (0,2,1) or better and scoped through 'header', per the
     specificity note above. Every :hover has a matching :focus-visible. */

  /* The desktop nav row, value for value from the reference's .sf-links: 22px
     gaps, 14px/500 type, muted resting color, near-black for hover and for the
     page you are on. (The links previously carried "ml-8 text-gray-700", which
     disagreed on all four and pushed the row 32px off centre.)

     Restated under the storefront's own class rather than reusing ".sf-links":
     the injected sheet carries a "@media (max-width:860px){.sf-links{display:
     none}}" that would apply on the home route only.

     (0,2,1), which clears Tailwind's (0,2,0) hover utility; the dark bar's
     "header.sf-head-dark a:hover" is (0,2,2) and still wins, which is what keeps
     the accent bar's hover white. */
  header .sf-navlinks { gap: 22px; font-size: 14px; font-weight: 500; }
  header .sf-navlink {
    color: #565E64;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color .14s;
  }
  /* The page you are on is the reference's "a.on" — the same near-black as
     hover. Nothing changes weight or size, so the row does not re-measure as
     you move across it. */
  header .sf-navlink.on,
  header .sf-navlink:hover,
  header .sf-navlink:focus-visible { color: #2C2F32; }
  header.sf-head-dark .sf-navlink.on { color: #FFFFFF; }
  header .sf-navlink:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 4px; border-radius: 3px; }

  /* The store name and logo are the way home, so they answer the pointer too.
     Opacity rather than a color: the name takes the store's own heading font
     and color, and the logo is a photo. */
  header .sf-brandlink { transition: opacity .14s; }
  header .sf-brandlink:hover,
  header .sf-brandlink:focus-visible { opacity: 0.78; }
  header .sf-brandlink:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 3px; border-radius: 4px; }

  /* The chips take the reference's icon-button hover — neutral wash, firmer
     ring, glyph stays muted rather than going orange. */
  header .sf-ic { transition: background-color .14s, border-color .14s, color .14s; }
  header .sf-ic:hover,
  header .sf-ic:focus-visible { background: #F5F6F6; border-color: #CDD1D4; color: #2C2F32; }
  header .sf-ic:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }
  /* On the dark bar "header.sf-head-dark button:hover" already whitens the
     glyph; only the fill and ring need restating, above the light rule. */
  header.sf-head-dark .sf-ic:hover,
  header.sf-head-dark .sf-ic:focus-visible {
    background: rgba(255,255,255,0.26);
    border-color: rgba(255,255,255,0.5);
  }
  header.sf-head-dark .sf-ic:focus-visible { outline-color: #FFFFFF; }

  header .sf-burger { transition: background-color .14s, border-color .14s; }
  header .sf-burger:hover,
  header .sf-burger:focus-visible { background: #F5F6F6; border-color: #CDD1D4; }
  header .sf-burger:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }
  header.sf-head-dark .sf-burger:hover,
  header.sf-head-dark .sf-burger:focus-visible { background: #F5F6F6; border-color: #CDD1D4; }

  /* The search strip's submit button. Its fill is the store's color, written
     inline, so the hover is a brightness step rather than a color swap. */
  header .sf-searchbtn { transition: filter .14s; }
  header .sf-searchbtn:hover,
  header .sf-searchbtn:focus-visible { filter: brightness(0.92); }
  header .sf-searchbtn:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }

  /* The categories panel's POSITION, stated here rather than left to utilities
     (absolute, left-1/2, -translate-x-1/2, top-full, -mt-[10px]) — the
     specificity note above, which showed up as a panel shifted half its width
     off the trigger on "/" only.

     Values are SP-main's own ".sf-menu": position:absolute; top:100%; left:50%;
     transform:translateX(-50%). The reference nudges DOWN 4px from the bar;
     this bar is 64px tall and the panel hangs off its bottom edge, so the 10px
     pull-up is kept as the one deliberate departure.

     ⚠ ONE OWNER FOR THE TRANSFORM. Do not put "-translate-x-1/2" back on the
     element: two sources composing into one "transform" is what produced the
     wrong offset. */
  header .sf-menu {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: -10px;
    z-index: 50;
  }

  /* The categories panel and the search results already tint rows on hover
     through utilities; these add the keyboard half. */
  header .sf-panelrow:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: -2px; border-radius: 8px; }
  header .sf-resultrow { transition: background-color .14s; }
  header .sf-resultrow:hover { background: #F5F6F6; }
  header .sf-resultrow:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: -2px; }
`;

const Header = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    useManageKey();

    useEffect(() => {
        const affiliateId = searchParams.get('affiliateId');
        const affiliateName = searchParams.get('affiliateName');
        if (affiliateId || affiliateName) {
            setAffiliateData(affiliateId, affiliateName);
        }
    }, [searchParams]);

    const { storeDetails, cartItems, error, pageDetails, headerMenuCategories, shouldRedirectToComingSoon } = useStore();
    const [productFilters, setProductFilters] = useState(null);
    const navigate = useNavigate();
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [hasCartItems, setHasCartItems] = useState(false);
    const [totalCartItem, SettotalCartItem] = useState(false);
    // Add this state at the top of your component
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
    const notLaunched = storeDetails?.status && storeDetails?.status !== 'INITIAL_PROCESS_COMPLETED_AND_LAUNCHED';
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((open) => {
            if (open) setIsMobileCategoriesOpen(false);
            return !open;
        });
    };

    /* With no scrim to tap, the ways out are the burger, Escape, and arriving
       somewhere — a menu left open across a route change would cover the page
       the shopper just asked for. */
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsMobileCategoriesOpen(false);
    }, [location.pathname, location.search]);

    /* A resize past the nav breakpoint takes the burger with it, leaving an
       open list with no control that closes it. Rotating a phone counts, so any
       width change closes it. */
    useEffect(() => {
        if (!isMobileMenuOpen) return;
        /* Width only: a phone browser fires resize when its address bar
           collapses on scroll, and closing the list under the scrolling thumb
           would be its own bug. */
        let lastWidth = window.innerWidth;
        const onResize = () => {
            if (window.innerWidth === lastWidth) return;
            lastWidth = window.innerWidth;
            setIsMobileMenuOpen(false);
            setIsMobileCategoriesOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isMobileMenuOpen]);

    useEffect(() => {
        if (!isMobileMenuOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
                setIsMobileCategoriesOpen(false);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isMobileMenuOpen]);
    useEffect(() => {
        if (notLaunched) {
            setHasCartItems(false);
            SettotalCartItem(0)
        }
    })
    useEffect(() => {
        setHasCartItems(cartItems?.length > 0);
        let tmep = 0;
        cartItems.map((item) => {
            tmep += item.cart_items.length
        })
        SettotalCartItem(tmep)
    }, [cartItems]);

    const handleCartClick = () => {
        setIsCartOpen(true);
    };

    const handleClose = () => {
        setIsCartOpen(false);
    }

    useEffect(() => {
        // navigate('/store-not-available')
    }, [error])

    useEffect(() => {
        if (storeDetails?.store_name) {
            document.title = storeDetails?.store_name + ` 's Online Store`;

        }
    }, [storeDetails?.store_name]);

    const [activeDropdown, setActiveDropdown] = useState(null);
    const [hoveredCategory, setHoveredCategory] = useState(null);

    const animalTags = headerMenuCategories?.find(g => g.tag_group.code === "animal_type")?.tags || [];

    // Get all unique categories across all animals
    const getAllCategories = () => {
        const categoriesMap = new Map();
        animalTags.forEach(tag => {
            tag.categories?.forEach(cat => {
                if (!categoriesMap.has(cat.id)) {
                    categoriesMap.set(cat.id, cat.name, cat.code);
                }
            });
        });
        return Array.from(categoriesMap.entries()).map(([id, name, code]) => ({ id, name, code }));
    };

    /* The Style tab stores a bare family name, and the ones with a space
       ("General Sans") are invalid CSS unquoted — same rule as bannerOptions()
       in StoreBuilder. A fully bracketed tagline is a seed placeholder. */
    const headingFontFamily = pageDetails?.page_data?.colorAndApperances?.font
        ? `'${pageDetails.page_data.colorAndApperances.font}', sans-serif`
        : '';
    const rawTagline = pageDetails?.page_data?.logoIdentity?.tagline;
    const storeTagline = rawTagline && !/^\[.*\]$/.test(String(rawTagline).trim())
        ? String(rawTagline).trim()
        : '';

    const [searchTerm, setSearchTerm] = useState('');
    const [rawSearch, setRawSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [showSearcInput, setShowSearcInput] = useState(false);
    const [ghostText, setGhostText] = useState('');
    const searchRef = useRef(null);
    const searchInputRef = useRef(null);
    const mobileSearchInputRef = useRef(null);
    const suggestionFullTextRef = useRef('');

    const isSearchInputActive = useCallback(() => {
        const active = document.activeElement;
        return active === searchInputRef.current || active === mobileSearchInputRef.current;
    }, []);
    const autocompleteCancelRef = useRef(null);
    const latestAutocompleteReqRef = useRef(0);
    const searchTermRef = useRef(searchTerm);
    const rawSearchRef = useRef(rawSearch);
    searchTermRef.current = searchTerm;
    rawSearchRef.current = rawSearch;

    const cancelInFlightAutocomplete = useCallback(() => {
        latestAutocompleteReqRef.current += 1;
        if (autocompleteCancelRef.current) {
            autocompleteCancelRef.current.cancel('Cancelled due to new autocomplete request');
            autocompleteCancelRef.current = null;
        }
    }, []);

    const debouncedSetSearch = useCallback(
        debounce((val) => {
            if (val !== searchTermRef.current) setSearchTerm(val);
        }, 500),
        []
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                // if (!isMobile) {
                setShowSearcInput(false);
                // }
            }
        };

        if (showSearcInput) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showSearcInput]);

    useEffect(() => {
        if (!showSearcInput) return;
        // One field at every width now — the search row under the bar.
        requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    }, [showSearcInput]);


    const fetchProductList = useCallback(async () => {
        if (!searchTerm) {
            setProducts([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            if (isSearchEnabled()) {
                const result = await searchProducts(searchTerm, {
                    filters: SEARCH_FILTERS,
                    sort: 'title:asc',
                    limit: 20,
                    offset: 0,
                });
                const hits = result?.data?.items || result?.items || [];
                setProducts(hits);
            } else {
                const filterArr = [];
                const searchKeyword = searchTerm;
                const response = await fetchProductsWithFallback({
                    paging: { limit: 20, offset: 0 },
                    sort: [],
                    ...(filterArr.length ? { filter: filterArr } : null),
                    ...(searchKeyword ? { query: { searchKeyword } } : {})
                });
                setProducts(response.data.data.items);
            }

        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm]);

    const getAllProductsFilter = async () => {
        const response = await axios.post(
            `${API_END_POINT.PRODUCT_FILTERS}`,
            {}
        );
        setProductFilters(response.data.data?.availableFilters?.categories)
    }

    const suggestionLabelLookup = React.useMemo(
        () => buildSuggestionLabelLookup(productFilters ? { categories: productFilters } : null),
        [productFilters]
    );

    const runAutocomplete = useCallback(async (val) => {
        if (!val || !isSearchEnabled() || /\s$/.test(val)) {
            cancelInFlightAutocomplete();
            suggestionFullTextRef.current = '';
            setGhostText('');
            return;
        }
        cancelInFlightAutocomplete();
        const requestId = latestAutocompleteReqRef.current;
        const source = axios.CancelToken.source();
        autocompleteCancelRef.current = source;
        try {
            const result = await autocomplete(val, {
                limit: 1,
                offset: 0,
                filters: SEARCH_FILTERS,
                index: 'products',
                cancelToken: source.token,
            });
            if (requestId !== latestAutocompleteReqRef.current) return;
            if (val !== rawSearchRef.current) return;
            if ((searchInputRef.current || mobileSearchInputRef.current) && !isSearchInputActive()) return;

            let ahead = '';
            const hits = result?.data?.hits || result?.hits || [];
            if (hits.length > 0) {
                const highlightedText = getMatchedHighlightText(hits[0]);
                const displayText = formatSuggestionDisplayText(highlightedText, suggestionLabelLookup);
                ahead = getSuggestionAheadText(val, highlightedText, displayText, suggestionLabelLookup);
                suggestionFullTextRef.current = getSuggestionTabValue(
                    val, highlightedText, displayText, ahead, suggestionLabelLookup
                );
            } else {
                suggestionFullTextRef.current = '';
            }
            setGhostText(ahead);
        } catch (error) {
            if (axios.isCancel && axios.isCancel(error)) return;
            if (requestId !== latestAutocompleteReqRef.current) return;
            suggestionFullTextRef.current = '';
            setGhostText('');
        }
    }, [cancelInFlightAutocomplete, suggestionLabelLookup, isSearchInputActive]);

    const debouncedAutocomplete = useCallback(
        debounce(runAutocomplete, 200, { maxWait: 600 }),
        [runAutocomplete]
    );

    // Accept ghost suggestion on Tab; dismiss on Escape
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Tab' && (ghostText || suggestionFullTextRef.current)) {
            e.preventDefault();
            debouncedSetSearch.cancel();
            debouncedAutocomplete.cancel();
            cancelInFlightAutocomplete();
            const newValue = getSuggestionCompletedValue(
                rawSearch,
                ghostText,
                suggestionFullTextRef.current
            );
            setRawSearch(newValue);
            setSearchTerm(newValue);
            suggestionFullTextRef.current = '';
            setGhostText('');
        } else if (e.key === 'Escape') {
            suggestionFullTextRef.current = '';
            setGhostText('');
        }
    };

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
            debouncedAutocomplete.cancel();
            cancelInFlightAutocomplete();
        };
    }, [debouncedSetSearch, debouncedAutocomplete, cancelInFlightAutocomplete]);

    useEffect(() => {
        fetchProductList();
        getAllProductsFilter();
    }, [fetchProductList]);

    const animals = [{
        "id": 1,
        "name": "Dog",
        "code": "dog",
    }, {
        "id": 2,
        "name": "Cat",
        "code": "cat",
    }, {
        "id": 3,
        "name": "Small Pet",
        "code": "small_pet",
    }, {
        "id": 4,
        "name": "Horse",
        "code": "horse",
    }]
    const handleCategoryNavigation = (categoryCode, subcategoryCode = null) => {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('category', categoryCode);
        if (subcategoryCode) {
            currentParams.set('subcategory', subcategoryCode);
        } else {
            currentParams.delete('subcategory');
        }
        navigate(`/all-products?${currentParams.toString()}`);
        setActiveDropdown(null);
        setHoveredCategory(null);
    };

    /* The mobile rows, in the designer's order. Categories is the one row that
       is not a destination: it discloses its own list. The enabled flags are
       read as the desktop rail reads them, so a link turned off in the Store
       designer is off at every width. */
    const isNavLinkEnabled = (label) =>
        pageDetails?.page_data?.navLinks?.find((l) => l.label === label)?.enabled !== false;

    const mobileCategories = (productFilters || []).filter((c) => c?.count > 0);

    /* Which nav row is the page you are on. The reference cannot answer this —
       its canvas never navigates, so it always marks the FIRST link `on` — but a
       permanently lit "Home" on the Brands page is worse than no active state.
       Categories and Shop All share /all-products and are told apart by whether
       a category is selected. */
    const isCurrent = (row) => {
        const here = location.pathname.replace(/\/+$/, '') || '/';
        const inCategory = Boolean(searchParams.get('category'));
        switch (row) {
            case 'Home': return here === '/';
            case 'Shop All': return here === '/all-products' && !inCategory;
            case 'Categories': return here === '/all-products' && inCategory;
            case 'Brands': return here === '/all-brands' || here.startsWith('/brands');
            case 'Collections': return here === '/all-collections' || here.startsWith('/collections/');
            default: return false;
        }
    };

    const mobileNavRows = [
        pageDetails?.page_data?.homeEnabled !== false && { label: 'Home', link: '/', on: isCurrent('Home') },
        isNavLinkEnabled('Shop All') && { label: 'Shop All', link: '/all-products', on: isCurrent('Shop All') },
        /* A store with nothing stocked has nothing to disclose, and a row that
           opens an empty bordered block is worse than no row. */
        isNavLinkEnabled('Categories') && mobileCategories.length > 0 && { label: 'Categories', disclosure: true, on: isCurrent('Categories') },
        isNavLinkEnabled('Brands') && { label: 'Brands', link: '/all-brands', on: isCurrent('Brands') },
        isNavLinkEnabled('Collections') && { label: 'Collections', link: '/all-collections', on: isCurrent('Collections') },
    ].filter(Boolean);

    /* Current query params ride along and the link's own params win, as the
       drawer did — a category tap inside a filtered list keeps the filters. */
    const navigateFromMenu = (link) => {
        setIsMobileMenuOpen(false);
        setIsMobileCategoriesOpen(false);
        if (!link) return;
        const url = new URL(link, window.location.origin);
        // A custom nav link can point off the storefront entirely, the way the
        // desktop rail's target="_blank" allows; routing it would lose the host.
        if (url.origin !== window.location.origin) {
            window.open(url.href, '_blank', 'noopener');
            return;
        }
        const currentParams = new URLSearchParams(window.location.search);
        new URLSearchParams(url.search).forEach((value, key) => currentParams.set(key, value));
        navigate(`${url.pathname}?${currentParams.toString()}`);
    };

    return (
        <>
            <Helmet>
                <style>{HEADER_DARK_STYLES}</style>
                <style>{HEADER_STYLES}</style>
                <style>{MOBILE_NAV_STYLES}</style>
                <link rel="icon" href={storeDetails?.store_favicon || pageDetails?.page_data?.logoIdentity?.favicon || "/favicon.ico"} />
                <link rel="apple-touch-icon" href={storeDetails?.store_favicon || pageDetails?.page_data?.logoIdentity?.favicon || "/favicon.png"} />
            </Helmet>

            {/* Announcement Bar */}
            {
                /* Colored from the Style tab; hidden when `announcementBarEnabled` is false or the text is empty. */
                (pageDetails?.page_data?.announcementBarEnabled !== false)
                && String(pageDetails?.page_data?.announcementBarText || '').trim()
                && <div
                    style={{ ...announcementBarColors(pageDetails?.page_data?.colorAndApperances), padding: '10px 28px', fontSize: '13px', lineHeight: 1.4 }}
                    className="text-center">
                    {pageDetails?.page_data?.announcementBarText?.replace(/\[\[(\w+)\]\]/g, (match, name) => {
                        const affiliateData = getAffiliateData();
                        const cookieValue = name === 'affiliateId' ? affiliateData.affiliateId : name === 'affiliateName' ? affiliateData.affiliateName : null;
                        return searchParams.get(name) || cookieValue || match;
                    })}
                </div>
            }


            {/* Header */}
            {/* TODO:V2: if need to old skeleton use this */}
            {/* <header className={`${notLaunched ? 'cursor-not-allowed pointer-events-none' : ''} bg-white shadow-sm relative`}> */}
            {/* The header's surface is the Style tab's Header choice. Nothing on
                a colored bar can safely inherit, so the dark variant restates
                the text, icon and rule colors for everything inside it. */}
            <header className={`sf-head sticky top-0 z-50 ${(pageDetails?.page_data?.colorAndApperances?.headerStyle || 'white') === 'near-black' ? 'sf-head-dark' : 'bg-white'}`}>
                {/* Full bleed with a fixed gutter — the reference's .sf-navin
                    (`padding:15px 40px`, no cap), which the canvas also ships.
                    The `max-w-7xl mx-auto` that used to be here capped the row
                    at 1280, so on a 1920 window the brand started ~320px in
                    while the bar's own background ran edge to edge. The links
                    stay centred through the nav's flex-1 + justify-center.
                    20px below the nav breakpoint, matching .sf-mobrow. */}
                <div className="w-full px-[20px] nav:px-[40px]">
                    <>
                        {(!pageDetails && !shouldRedirectToComingSoon) ? (
                            <HeaderSkeleton />
                        ) : (
                            <div className={`flex items-center justify-between h-16 ${shouldRedirectToComingSoon ? 'hidden' : ''}`}>
                                {/* Brand — logo tile, store name, tagline, from the reference's
                                        .sf-brand. min-w-0 + truncate so a long store name ellipses
                                        instead of pushing the tools off the right edge. */}
                                <div className="flex items-center gap-[11px] min-w-0 flex-[0_1_auto]">
                                    <span
                                        onClick={() => navigate("/")}
                                        className="sf-brandlink relative h-[38px] w-[38px] nav:h-[40px] nav:w-[40px] rounded-[8px] flex-none cursor-pointer grid place-items-center overflow-hidden"
                                        style={(storeDetails?.store_logo || pageDetails?.page_data?.logoIdentity?.storeLogo) ? undefined : {
                                            background: ((pageDetails?.page_data?.colorAndApperances?.headerStyle || 'white') === 'near-black') ? 'rgba(255,255,255,0.18)' : (pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439'),
                                            border: `1px solid ${((pageDetails?.page_data?.colorAndApperances?.headerStyle || 'white') === 'near-black') ? 'rgba(255,255,255,0.4)' : 'transparent'}`
                                        }}
                                    >
                                        {(storeDetails?.store_logo || pageDetails?.page_data?.logoIdentity?.storeLogo) ? (
                                            <img
                                                src={storeDetails?.store_logo || pageDetails?.page_data?.logoIdentity?.storeLogo}
                                                alt={storeDetails?.store_name || "Store Logo"}
                                                className="absolute inset-0 w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span style={{ color: ((pageDetails?.page_data?.colorAndApperances?.headerStyle || 'white') === 'near-black') ? '#ffffff' : readableOn(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439'), fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.05em' }}>
                                                {(storeDetails?.store_name || 'Shopdot').split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "SD"}
                                            </span>
                                        )}
                                    </span>
                                    {storeDetails?.store_name && (
                                        <span className="min-w-0 max-w-[150px] md:max-w-[250px] lg:max-w-[350px] xl:max-w-[450px]">
                                            <span
                                                onClick={() => navigate("/")}
                                                style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}
                                                className="sf-brandlink block truncate font-bold text-[15px] nav:text-[17px] leading-[1.3] tracking-[-0.01em] cursor-pointer"
                                            >
                                                {storeDetails.store_name}
                                            </span>
                                            {storeTagline && (
                                                <span className="sf-btag block truncate text-[10px] font-semibold uppercase tracking-[0.13em] mt-[1px]">
                                                    {storeTagline}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>

                                {/* Logo */}


                                {/* Desktop Navigation */}
                                {/* nav is h-16 — the bar's own height — so the Categories panel
                                    hangs off the bar's bottom edge, not off the 24px-tall link.
                                    flex-1 + justify-center + nowrap is .sf-links: the links take
                                    the middle and never wrap, which pins the tools to the right
                                    edge. Gap, type and colors are in HEADER_STYLES under
                                    .sf-navlinks / .sf-navlink. */}
                                {!shouldRedirectToComingSoon && (
                                    <nav className="sf-navlinks hidden nav:flex items-center h-16 flex-1 justify-center whitespace-nowrap">
                                        {pageDetails?.page_data?.homeEnabled !== false && <Link to="/" className={`sf-navlink${isCurrent('Home') ? ' on' : ''}`} aria-current={isCurrent('Home') ? 'page' : undefined}>Home</Link>}
                                        {pageDetails?.page_data?.navLinks?.find(l => l.label === 'Shop All')?.enabled !== false && <Link to="/all-products" className={`sf-navlink${isCurrent('Shop All') ? ' on' : ''}`} aria-current={isCurrent('Shop All') ? 'page' : undefined}>Shop All</Link>}
                                        {/* {storeDetails?.user?.business_vertical?.code === 'pet' && ( */}
                                        {pageDetails?.page_data?.navLinks?.find(l => l.label === 'Categories')?.enabled !== false && <div className="flex items-center h-full">
                                            <div
                                                className={`relative flex items-center h-full ${activeDropdown === 'categories' ? "after:content-[''] after:absolute after:top-full after:inset-x-0 after:h-2" : ''}`}
                                                onMouseEnter={() => setActiveDropdown('categories')}
                                                onMouseLeave={() => {
                                                    setActiveDropdown(null);
                                                    setHoveredCategory(null);
                                                }}
                                            >
                                                {/* The trigger reads as one of the links, so it takes
                                                the link's own classes. The old pair here changed COLOR
                                                and WEIGHT when open, which re-measures the label and
                                                shoves the links to its right. */}
                                                <button
                                                    type="button"
                                                    className={`sf-navlink${activeDropdown === 'categories' || isCurrent('Categories') ? ' on' : ''}`}
                                                    aria-expanded={activeDropdown === 'categories'}
                                                >
                                                    <span>Categories</span>
                                                    <ChevronDown className="w-[13px] h-[13px]" />
                                                </button>
                                                {/* Panel = SP-main's .sf-menu: a single-column list centered
                                                under the trigger, one rounded white surface with a hairline
                                                border and soft shadow, each row a full-width button that
                                                tints on hover, closed by "Shop everything" under a
                                                separator. The trigger's ::after (below) covers the gap under
                                                the bar so the pointer does not leave the trigger on its way
                                                down.

                                                A category with its own `subCategories` — the same array
                                                SideFilter/Products read off each filter category — opens a
                                                second panel flush to this one's right edge while that row is
                                                hovered. `tag.subCategories` is already the right list; do not
                                                re-look-up the hovered row. */}
                                                {activeDropdown === 'categories' && (
                                                    <div className="sf-menu">
                                                        <div className="relative min-w-[262px] max-h-[min(70vh,520px)] overflow-y-auto bg-white rounded-xl border border-[#E5E7E8] shadow-[0_16px_40px_rgba(20,17,15,0.14)] p-2">
                                                            {productFilters.filter(tag => tag?.count > 0).map(tag => {
                                                                const subs = (tag.subCategories || []).filter(s => s?.count > 0);
                                                                return (
                                                                    <button
                                                                        key={tag.id}
                                                                        type="button"
                                                                        onMouseEnter={() => setHoveredCategory(tag)}
                                                                        onClick={() => handleCategoryNavigation(tag.code)}
                                                                        className={`sf-panelrow w-full flex items-center justify-between gap-[18px] text-left py-[9px] px-[12px] rounded-[8px] text-[13.5px] font-medium text-[#2C2F32] transition-colors hover:bg-[#F5F6F6]`}
                                                                    >
                                                                        {tag.name}
                                                                    </button>
                                                                );
                                                            })}
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate('/all-products')}
                                                                style={{ color: accentTextOf(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439') }}
                                                                className="sf-panelrow w-full flex items-center gap-[6px] text-left mt-[4px] py-[12px] px-[12px] border-t border-[#E5E7E8] rounded-b-[8px] text-[13.5px] font-semibold hover:bg-[#F5F6F6]"
                                                            >
                                                                Shop everything <ArrowRight className="w-[14px] h-[14px]" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>}
                                        {/* )} */}
                                        {/* <Link to="/all-products" className="sf-navlink ml-8 text-gray-700">Categories</Link> */}
                                        {pageDetails?.page_data?.navLinks?.find(l => l.label === 'Brands')?.enabled !== false && <Link to="/all-brands" className={`sf-navlink${isCurrent('Brands') ? ' on' : ''}`} aria-current={isCurrent('Brands') ? 'page' : undefined}>Brands</Link>}
                                        {pageDetails?.page_data?.navLinks?.find(l => l.label === 'Collections')?.enabled !== false && <Link to="/all-collections" className={`sf-navlink${isCurrent('Collections') ? ' on' : ''}`} aria-current={isCurrent('Collections') ? 'page' : undefined}>Collections</Link>}
                                        {pageDetails?.page_data?.navLinks?.map((navLink, index) => (
                                            navLink.enabled !== false && navLink.label?.trim() && !["Today's Deals", "Shop All", "Categories", "Brands", "Collections"].includes(navLink.label) && <Link
                                                to={navLink.link}
                                                target='_blank'
                                                className="sf-navlink">
                                                {navLink.label}
                                            </Link>
                                        ))}
                                    </nav>
                                )}

                                {/* Desktop Search and Cart. The circles are the reference's
                                    .sf-ic: 38px, hairline ring, muted glyph; the dark bar's
                                    translucent variant is in HEADER_DARK_STYLES. */}
                                {!shouldRedirectToComingSoon && (
                                    <div className="hidden nav:flex items-center gap-[10px] flex-none ml-auto">
                                        {/* The bar carries only the toggle; the field is the
                                        full-width row under the header. */}
                                        <button
                                            type="button"
                                            aria-label="Search"
                                            aria-expanded={showSearcInput}
                                            onClick={(e) => { e.stopPropagation(); setShowSearcInput((v) => !v); }}
                                            className="sf-ic"
                                        >
                                            <Search />
                                        </button>

                                        <button
                                            type="button"
                                            aria-label="Cart"
                                            onClick={handleCartClick}
                                            className="sf-ic"
                                        >
                                            <ShoppingCart />

                                            {cartItems?.length > 0 && (
                                                <span
                                                    style={{ backgroundColor: pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439', color: readableOn(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439') }}
                                                    className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center"
                                                >
                                                    {totalCartItem > 99 ? '99+' : totalCartItem}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                )}




                                {/* Same chips as the desktop rail, in the reference's order:
                                    search, cart, burger last, nearest the thumb. flex-1 +
                                    justify-end is the reference's `.sf-tools{margin-left:auto}`:
                                    with the links hidden there is no flex:1 spacer, so the tools
                                    have to claim the gap or they slide back under the store name. */}
                                <div className="flex items-center justify-end gap-[8px] nav:hidden flex-1">
                                    <button
                                        type="button"
                                        aria-label="Search"
                                        aria-expanded={showSearcInput}
                                        onClick={(e) => { e.stopPropagation(); setShowSearcInput((v) => !v); }}
                                        className="sf-ic"
                                    >
                                        <Search />
                                    </button>

                                    <button
                                        type="button"
                                        aria-label="Cart"
                                        onClick={handleCartClick}
                                        className="sf-ic"
                                    >
                                        <ShoppingCart />

                                        {cartItems?.length > 0 && (
                                            <span
                                                style={{ backgroundColor: pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439', color: readableOn(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439') }}
                                                className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center"
                                            >
                                                {totalCartItem > 99 ? '99+' : totalCartItem}
                                            </span>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        aria-label="Menu"
                                        aria-expanded={isMobileMenuOpen}
                                        aria-controls="sf-mobnav"
                                        onClick={toggleMobileMenu}
                                        className="sf-burger"
                                    >
                                        <Menu />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                </div>
                {/* Search row. The reference opens search as its own white strip
                    beneath the bar rather than squeezing a field into it, so the bar
                    keeps only the toggle. Only the frame is new. */}
                {!shouldRedirectToComingSoon && showSearcInput && (
                    <div className="sf-search-row bg-white border-t border-[#E5E7E8]">
                        <div
                            ref={searchRef}
                            /* Same gutter as the bar above. The ROW is full
                               bleed and the FIELD carries its own cap, as the
                               reference does — the field is capped at 590px
                               below and the form centres it. */
                            className="w-full px-[20px] nav:px-[40px] py-[12px]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* justify-center, and the field capped rather than flexed:
                                the row is one search control in the middle of the page,
                                not three elements spread across it. */}
                            <form
                                role="search"
                                className="flex items-center justify-center gap-[8px]"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    debouncedSetSearch.cancel();
                                    setSearchTerm(rawSearch);
                                }}
                            >
                                <Search className="w-[20px] h-[20px] text-[#565E64] flex-none mr-[4px]" />
                                {/* The results list hangs off this wrapper, not the row,
                                    so it stays the width of the field it belongs to. */}
                                <div className="relative w-full max-w-[590px] min-w-0">
                                    <div className="relative h-[40px] flex items-center overflow-hidden rounded-[6px] border border-[#CDD1D4] bg-white px-[14px]">
                                        {ghostText && rawSearch && (
                                            <div
                                                aria-hidden="true"
                                                className="absolute inset-y-0 left-[14px] right-[14px] flex items-center pointer-events-none"
                                                style={{ fontFamily: 'inherit', fontSize: '16px', fontWeight: '400', letterSpacing: 'inherit', whiteSpace: 'pre' }}
                                            >
                                                <span style={{ color: 'transparent' }}>{rawSearch}</span>
                                                <span style={{ color: '#AAB2B6' }}>{ghostText}</span>
                                            </div>
                                        )}
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={rawSearch}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setGhostText('');
                                                suggestionFullTextRef.current = '';
                                                setRawSearch(val);
                                                debouncedSetSearch(val);
                                                debouncedAutocomplete(val);
                                            }}
                                            onKeyDown={handleSearchKeyDown}
                                            placeholder="Search products and brands"
                                            autoComplete="off"
                                            aria-autocomplete="inline"
                                            aria-label="Search products and brands"
                                            className="w-full text-[16px] font-normal text-primary-text placeholder-[#AAB2B6] bg-transparent outline-none relative z-10"
                                        />
                                    </div>

                                    {rawSearch && (
                                        <div className="absolute left-0 right-0 top-full mt-[6px] max-h-[300px] overflow-y-auto flex flex-col bg-white rounded-[4px] border border-[#CDD1D4] shadow-[0_12px_16px_-4px_rgba(44,47,50,0.08)] z-50">
                                            {isLoading ? <SearchLoader /> : products.map((product) => (
                                                <div
                                                    key={product.id}
                                                    onClick={() => {
                                                        setRawSearch('');
                                                        navigate(`product/${product.id}`);
                                                    }}
                                                    className="sf-resultrow w-full p-[16px] flex items-center cursor-pointer"
                                                >
                                                    <div className="flex gap-[8px] items-center w-full min-w-0">
                                                        <img
                                                            src={
                                                                product?.product_images?.find(img => img.is_main === "1")?.src ||
                                                                product?.product_images?.[0]?.src ||
                                                                logoShopdot
                                                            }
                                                            alt={product.title}
                                                            className="w-[38px] h-[38px] object-contain rounded-md"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <h2 className="text-[16px] text-[#2C2F32] font-medium truncate">{product.title}</h2>
                                                            <p className="text-[12px] text-[#2c2f32] truncate">{getProductStoreName(product)}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    style={{ backgroundColor: pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439', color: readableOn(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439'), border: `1px solid ${lightToneOf(pageDetails?.page_data?.colorAndApperances?.colorTheme || '#E15439') || 'transparent'}` }}
                                    className="sf-searchbtn flex-none h-[40px] px-[22px] rounded-[4px] text-[14px] font-medium"
                                >
                                    Search
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Mobile navigation. The designer's inline list, not a panel over
                    the page: it opens under the bar, full width, and the page moves
                    down for it. The burger toggles it, Escape closes it. */}
                {!shouldRedirectToComingSoon && isMobileMenuOpen && (
                    <nav
                        id="sf-mobnav"
                        className="sf-mobnav nav:hidden"
                        aria-label="Store navigation"
                        style={{ '--sf-accent': accentTextOf(pageDetails?.page_data?.colorAndApperances?.colorTheme || DEFAULT_THEME) }}
                    >
                        {mobileNavRows.map((row) => (
                            row.disclosure ? (
                                <React.Fragment key={row.label}>
                                    <button
                                        type="button"
                                        aria-expanded={isMobileCategoriesOpen}
                                        aria-controls="sf-mobcats"
                                        onClick={() => setIsMobileCategoriesOpen((v) => !v)}
                                        className={`sf-mobrow${row.on ? ' is-on' : ''}`}
                                    >
                                        <span>{row.label}</span>
                                        <ChevronDown />
                                    </button>
                                    {isMobileCategoriesOpen && (
                                        <div id="sf-mobcats" className="sf-mobcats">
                                            {mobileCategories.map((cat) => {
                                                const on = location.pathname.replace(/\/+$/, '') === '/all-products'
                                                    && searchParams.get('category') === String(cat.code);
                                                return (
                                                    <button
                                                        key={cat.id || cat.code}
                                                        type="button"
                                                        className={`sf-mobcat${on ? ' is-on' : ''}`}
                                                        aria-current={on ? 'page' : undefined}
                                                        onClick={() => navigateFromMenu(`/all-products?category=${cat.code}`)}
                                                    >
                                                        <span className="nm">{cat.name}</span>
                                                        {cat.count > 0 && <span className="ct">{cat.count}</span>}
                                                        <ChevronRight aria-hidden="true" />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </React.Fragment>
                            ) : (
                                <button
                                    key={row.label}
                                    type="button"
                                    onClick={() => navigateFromMenu(row.link)}
                                    className={`sf-mobrow${row.on ? ' is-on' : ''}`}
                                >
                                    <span>{row.label}</span>
                                </button>
                            )
                        ))}

                        {pageDetails?.page_data?.navLinks?.map((navLink, index) => (
                            navLink.enabled !== false && navLink.label?.trim() && !["Today's Deals", "Shop All", "Categories", "Brands", "Collections"].includes(navLink.label) && (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => navigateFromMenu(navLink.link)}
                                    className="sf-mobrow"
                                >
                                    <span>{navLink.label}</span>
                                </button>
                            )
                        ))}
                    </nav>
                )}
            </header>


            <CartView isOpen={isCartOpen} onClose={handleClose} />
        </>
    );
};

export default Header;
export function SearchLoader() {
    return (
        <div className="flex items-center gap-4 p-4">
            {/* Product Image Skeleton */}
            <div className="w-[38px] h-[38px] bg-gray-200 rounded animate-pulse flex-shrink-0" />

            {/* Product Title Skeleton */}
            <div className="flex-1">
                <div className="h-8 bg-gray-200 rounded animate-pulse max-w-xs" />
            </div>
        </div>
    );
}